import type { VehicleType } from "~/lib/constants/vehicle";

/** The destination fields a search URL carries, as Mapbox resolves them. */
export interface SearchPlace {
  lat: number;
  lng: number;
  name?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface SearchUrlInput {
  /** The typed/committed destination label — the `q` param. */
  q: string;
  place: SearchPlace | null;
  starts_at: string;
  ends_at: string;
  vehicleSize: VehicleType | null;
}

function isRunnable(input: SearchUrlInput): boolean {
  return !!(
    input.q.trim() &&
    input.starts_at &&
    input.ends_at &&
    input.vehicleSize
  );
}

/**
 * Set when there's a value, remove when there isn't. Editing patches the URL in
 * place, so a field the new destination lacks has to be actively removed —
 * skipping it would strand the previous destination's value. Moving from a POI to
 * a plain street address is the case that bites: the address has no `dest_name`,
 * and the POI's name would otherwise stay in the URL labelling it.
 */
function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | null | undefined,
): void {
  if (value) params.set(key, value);
  else params.delete(key);
}

function writeSearchFields(
  params: URLSearchParams,
  input: SearchUrlInput,
): void {
  params.set("q", input.q.trim());
  params.set("starts_at", input.starts_at);
  params.set("ends_at", input.ends_at);
  // `isRunnable` has already rejected a null size; this keeps TS honest.
  if (input.vehicleSize) params.set("vehicle_size", input.vehicleSize);

  setOrDelete(params, "lat", input.place ? String(input.place.lat) : null);
  setOrDelete(params, "lng", input.place ? String(input.place.lng) : null);
  setOrDelete(params, "city", input.place?.city);
  setOrDelete(params, "state", input.place?.state);
  setOrDelete(params, "dest_name", input.place?.name);
}

/**
 * The search params for a *fresh* search. For the landing card, which has no URL
 * to preserve. Returns null when the search isn't complete enough to run, which
 * callers use as their submit guard.
 */
export function buildSearchUrl(input: SearchUrlInput): string | null {
  if (!isRunnable(input)) return null;
  const params = new URLSearchParams();
  writeSearchFields(params, input);
  return `/search?${params.toString()}`;
}

/**
 * `current` with the search fields replaced — for the editors on `/search`, which
 * are changing one search into another.
 *
 * A patch rather than a rebuild because the URL holds more than this input knows
 * about: the filters, and anything a link carried in (campaign and tracking
 * params). Rebuilding from scratch dropped all of it, and — because the result
 * could then never match the address bar — also broke `isSameSearch`, so an
 * untouched close looked like a change and pushed a duplicate search.
 *
 * Returns null on the same guard as `buildSearchUrl`.
 */
export function applySearchToParams(
  input: SearchUrlInput,
  current: URLSearchParams,
): URLSearchParams | null {
  if (!isRunnable(input)) return null;
  const next = new URLSearchParams(current.toString());
  writeSearchFields(next, input);
  return next;
}

/**
 * Key order and empty values carry no meaning here: the two sides are written by
 * different code paths, and `?city=` says exactly what an absent `city` says. Both
 * are normalised away before comparing.
 */
function normalize(params: URLSearchParams): string {
  const out = new URLSearchParams();
  for (const [key, value] of params) {
    if (value !== "") out.append(key, value);
  }
  out.sort();
  return out.toString();
}

/**
 * Whether two param sets describe the same search.
 *
 * This is what makes commit-on-close free: the pill commits its draft whenever a
 * panel closes, and a close that changed nothing has to stop here rather than
 * push a duplicate history entry and refetch.
 */
export function isSameSearch(a: URLSearchParams, b: URLSearchParams): boolean {
  return normalize(a) === normalize(b);
}

/** Everything that names where the user is parking. */
const DESTINATION_PARAMS = [
  "q",
  "lat",
  "lng",
  "city",
  "state",
  "dest_name",
] as const;

/**
 * Whether two param sets point at the same destination.
 *
 * Decides push versus replace. A new place is somewhere the user has been and may
 * want to go Back to; re-timing or re-sizing is a refinement of the place they are
 * already looking at, so it overwrites that entry instead of stacking on it.
 */
export function isSameDestination(
  a: URLSearchParams,
  b: URLSearchParams,
): boolean {
  return DESTINATION_PARAMS.every(
    (key) => (a.get(key) ?? "") === (b.get(key) ?? ""),
  );
}
